import React, { useEffect, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { Redirect, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { ProxiedRoutes, QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import SpecialLifeAtButton from "@common/LifeAtButton/Variants/SpecialLifeAtButton";
import LifeAtModal from "@common/LifeAtModal/LifeAtModal";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { buildDeepLink } from "@util/DeepLinkUtil";
import { BrowserUtil, RouteUtil } from "@util/index";
import * as RoomService from "../../features/room/RoomService";
import { updateLastVisitedRooms } from "../../features/room/RoomService";
import useRoomMemberInfoListener from "../../features/room/useRoomMemberInfoListener";
import UserAccountModal from "../../features/user/components/UserAccountModal/UserAccountModal";

const RoomAccessPageWrapper = styled.div`
    position: absolute;
    display: block;
    height: 100vh;
    width: 100vw;
    padding: 50px;
    background-color: beige;
`;

const EnterRoomButton = styled(SpecialLifeAtButton)`
    width: auto;
`;

const ReturnToYourRoomBtn = styled.span`
    position: absolute;
    top: 50px;
    left: 50px;

    height: auto;
    width: auto;

    color: white;
    z-index: 10000;
    &:hover {
        cursor: pointer;
        text-decoration: underline;
    }
`;

const ROOM_ACCESS_MODAL_BTN_LABEL = "Sign Up";
const BACK_TO_YOUR_ROOM_LABEL = "←️ Back to your room";
const DONT_HAVE_DESKTOP_APP_LABEL = "Don't have the LifeAt Desktop App yet?";
const DOWNLOAD_DESKTOP_APP_BTN_LABEL = "Download now";
const getRoomAuthModalTitle = () => {
    return RouteUtil.queryParamPresent(QueryParameters.RoomHost)
        ? `Sign up to enter ${RouteUtil.getQueryParamValue(QueryParameters.RoomHost)} 💫`
        : "Sign up to enter this Room 💫";
};

interface RoomAccessPageParams {
    readonly roomId: string;
}
export default function RoomAccessPage() {
    const { roomId }: RoomAccessPageParams = useParams();

    const { user, userData } = useUserContext();

    const roomMemberInfo = useRoomMemberInfoListener(user?.uid, roomId);
    const isUserBannedDb = roomMemberInfo?.isBannedByHost ?? false;

    return (
        <RoomAccessPageWrapper>
            {!!user && !!userData && (
                <RoomAccessModal
                    roomId={roomId}
                    returnToOwnRoom={BrowserUtil.redirectPersonalSpace}
                    isUserBanned={isUserBannedDb}
                />
            )}

            <ReturnToYourRoomBtn onClick={BrowserUtil.redirectPersonalSpace}>
                {BACK_TO_YOUR_ROOM_LABEL}
            </ReturnToYourRoomBtn>

            {!user && !userData && (
                <LifeAtModal
                    title={getRoomAuthModalTitle()}
                    isOpen={true}
                    closeModal={() => {}}
                    centerContent={true}
                    preventClose={true}
                >
                    <UserAccountModal
                        buttonText={ROOM_ACCESS_MODAL_BTN_LABEL}
                        isSpecial={true}
                        onSuccessRedirectUrl={`/${Routes.RoomAccess}/${roomId}`}
                    />
                </LifeAtModal>
            )}
        </RoomAccessPageWrapper>
    );
}

const StyledLinkButton = styled(LinkButton)`
    font-size: 16px;
    color: #4a68d9;
`;

const DesktopAppDownloadCtaWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
    flex-wrap: wrap;
    text-align: center;
    width: 75%;
`;

const ROOM_LOCKED_TITLE = "This Room cannot be joined";
const ROOM_LOCKED_MESSAGE =
    "The host has locked this Room so new guests cannot join at this time. Please check in with the host.";

const USER_BANNED_TITLE = "This Room cannot be joined";
const USER_BANNED_MESSAGE =
    "Uh-oh. Looks like you’ve been banned. You’ll no longer be able to join or interact with this community.";

const ENTER_ROOM_BTN_LABEL = !window.todesktop ? "Join from browser" : "Enter Room";
const ENTER_ROOM_MODAL_TITLE = (hostName?: string) => {
    return hostName ? `Ready to join ${hostName}?` : "Ready to join the room?";
};
const ENTER_ROOM_MODAL_DISPLAY_DELAY = 500;

const RETURN_TO_ROOM_BTN_LABEL = "Return to your room";

interface RoomAccessModalProps {
    readonly roomId: string;
    readonly returnToOwnRoom: () => void;
    readonly isUserBanned: boolean;
}
function RoomAccessModal(props: RoomAccessModalProps) {
    const history = useHistory();

    const { user, userData }: UserContextType = useUserContext();

    const [showRoomLockedModal, setShowRoomLockedModal] = useState(false);
    const [showEnterRoomModal, setShowEnterRoomModal] = useState(false);

    useEffect(() => {
        setTimeout(() => setShowEnterRoomModal(true), ENTER_ROOM_MODAL_DISPLAY_DELAY);
    }, []);

    const isHost = isRoomOwnedByUser(props.roomId, userData);

    useEffect(() => {
        if (userData?.roomsData?.personalRoomId === props.roomId) {
            history.push({ pathname: `/${Routes.Room}/${props.roomId}` });
        }
    }, [userData]);

    useEffect(() => {
        if (user && props.roomId) {
            setShowEnterRoomModal(true);

            // Only check for lock status once, so that current room participants are not
            // evicted from the room if the host locks the room.
            RoomService.checkIfRoomLocked(props.roomId, (isLocked: boolean) => {
                // Automatically open the desktop app when room access page is viewed in browser
                if (!isLocked && !window.todesktop && !(isMobile || isTablet)) {
                    BrowserUtil.redirectTo(buildDeepLink(`${Routes.RoomAccess}/${props.roomId}`));
                }
                setShowRoomLockedModal(isLocked);
            });
        }
    }, [user, props.roomId]);

    const goToDownloadDesktopAppButton = !window.todesktop && !(isMobile || isTablet) && (
        <DesktopAppDownloadCtaWrapper>
            <span>{DONT_HAVE_DESKTOP_APP_LABEL}</span>
            <StyledLinkButton
                label={DOWNLOAD_DESKTOP_APP_BTN_LABEL}
                handleClick={() => BrowserUtil.openLinkNewTab(ProxiedRoutes.Download)}
            />
        </DesktopAppDownloadCtaWrapper>
    );

    const returnToRoomButton = (
        <StyledLinkButton label={RETURN_TO_ROOM_BTN_LABEL} handleClick={() => props.returnToOwnRoom()} />
    );

    const handleEnterRoom = async () => {
        if (user) {
            await updateLastVisitedRooms(user.uid, props.roomId);
        }
        history.push({
            pathname: `/${Routes.Room}/${props.roomId}`,
            search: RouteUtil.updateQueryParams(QueryParameters.EnterRoom, String(true)),
        });
    };

    const enterRoomButton = (
        <EnterRoomButton isLink={false} text={ENTER_ROOM_BTN_LABEL} handleClick={handleEnterRoom} variant={"blue"} />
    );

    const EnterRoomModal = showEnterRoomModal && !showRoomLockedModal && (
        <LifeAtModal
            title={ENTER_ROOM_MODAL_TITLE(RouteUtil.getQueryParamValue(QueryParameters.RoomHost))}
            isOpen={true}
            closeModal={() => {}} // cannot be closed
            preventClose={true}
            centerContent={true}
        >
            {enterRoomButton}

            {goToDownloadDesktopAppButton}
        </LifeAtModal>
    );

    const RoomLockedModal = showRoomLockedModal && (
        <LifeAtModal
            title={ROOM_LOCKED_TITLE}
            subtitle={ROOM_LOCKED_MESSAGE}
            isOpen={showRoomLockedModal}
            closeModal={() => {}} // cannot be closed
            preventClose={true}
            centerContent={true}
        >
            {returnToRoomButton}
        </LifeAtModal>
    );

    return !isHost ? (
        <>
            {props.isUserBanned && (
                <LifeAtModal
                    title={USER_BANNED_TITLE}
                    subtitle={USER_BANNED_MESSAGE}
                    isOpen={props.isUserBanned}
                    closeModal={() => {}} // cannot be closed
                    preventClose={true}
                    centerContent={true}
                >
                    {returnToRoomButton}
                </LifeAtModal>
            )}

            {!props.isUserBanned && (
                <>
                    {RoomLockedModal}
                    {EnterRoomModal}
                </>
            )}
        </>
    ) : (
        <Redirect to={`/${Routes.Room}/${props.roomId}`} />
    );
}
